import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function ConsumerUnitUpgradesPage() {
  useEffect(() => {
    document.title = 'Consumer Unit Upgrades Watford | Beyond Electrical Services';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional fuse box and consumer unit upgrades in Watford. 18th Edition compliant, RCD protection. NICEIC approved. Call +44 7989 675952 for a free quote.');
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      (canonical as HTMLLinkElement).rel = 'canonical';
      document.head.appendChild(canonical);
    }
    (canonical as HTMLLinkElement).href = `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/installations/consumer-unit-upgrades`;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'consumer-unit-schema';
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Beyond Electrical Services",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/installations/consumer-unit-upgrades`,
        "telephone": "+447989675952",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Watford",
          "addressRegion": "Hertfordshire",
          "postalCode": "WD17",
          "addressCountry": "GB"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 51.6565,
          "longitude": -0.3957
        },
        "areaServed": { "@type": "City", "name": "Watford" },
        "priceRange": "££"
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Consumer Unit Upgrades Watford",
        "description": "Professional fuse box and consumer unit upgrades in Watford. 18th Edition compliant installations with RCD protection by NICEIC approved electricians.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Beyond Electrical Services",
          "telephone": "+447989675952"
        },
        "areaServed": { "@type": "City", "name": "Watford" },
        "serviceType": "Consumer Unit Upgrades",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/installations/consumer-unit-upgrades`
      }
    ]);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('consumer-unit-schema');
      if (s) document.head.removeChild(s);
    };
  }, []);

  const areas = [
    { name: 'Chorleywood', slug: 'chorleywood' },
    { name: 'Moor Park & Northwood', slug: 'moor-park-northwood' },
    { name: 'Croxley Green', slug: 'croxley-green' },
    { name: 'Pinner', slug: 'pinner' },
    { name: 'Bushey', slug: 'bushey' },
    { name: 'Abbots Langley', slug: 'abbots-langley' },
    { name: 'Harrow', slug: 'harrow' },
    { name: 'Ruislip', slug: 'ruislip' },
    { name: 'Stanmore', slug: 'stanmore' },
    { name: 'Edgware', slug: 'edgware' },
    { name: 'Mill Hill', slug: 'mill-hill' },
    { name: 'Radlett', slug: 'radlett' },
    { name: 'Borehamwood', slug: 'borehamwood' },
    { name: 'Elstree', slug: 'elstree' },
    { name: 'Potters Bar', slug: 'potters-bar' },
    { name: 'South Oxhey', slug: 'south-oxhey' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]"></div>
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/e3dd1a67aa55c3a37fc69d11de00f3be.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-shield-check-fill"></i>
              18th Edition Compliant
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-map-pin-fill"></i>
              Watford Experts
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Consumer Unit Upgrades<br />
            <span className="text-[#FFD700]">in Watford</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Upgrade your outdated fuse box to a modern consumer unit with RCD protection. Professional consumer unit installation and replacement services throughout Watford and Rickmansworth.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-xl shadow-[#FFD700]/30 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill"></i>
              Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Consumer Unit Upgrade Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Complete consumer unit replacement and upgrade solutions for Watford properties
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🔧
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Fuse Box Replacement</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Replace your old-style fuse box with a modern consumer unit featuring MCBs and RCD protection for enhanced safety in your Watford home.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🛡️
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">RCD Protection</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Install residual current devices (RCDs) that instantly cut power in case of electrical faults, protecting Watford families from electric shocks.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  ⚡
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">18th Edition Compliance</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    All consumer unit upgrades meet BS 7671 18th Edition wiring regulations, ensuring your Watford property is fully compliant.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🔌
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Circuit Labeling</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Clear circuit labeling and documentation for easy identification of circuits in your Watford home's consumer unit.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  📋
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Full Certification</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    NICEIC electrical installation certificate provided for all consumer unit upgrades completed in Watford and surrounding areas.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🔍
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Electrical Testing</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Comprehensive electrical testing before and after installation to ensure safe operation of your new consumer unit in Watford.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Upgrade */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Why Upgrade Your Consumer Unit?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Key reasons to replace your old fuse box in Watford
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                🛡️
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Enhanced Safety</h3>
              <p className="text-gray-600 leading-relaxed">
                Modern consumer units with RCD protection provide life-saving protection against electric shocks and electrical fires in Watford homes.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                ⚡
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Better Protection</h3>
              <p className="text-gray-600 leading-relaxed">
                MCBs (miniature circuit breakers) are more reliable than old fuses and can be easily reset after tripping in your Watford property.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                📋
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Legal Compliance</h3>
              <p className="text-gray-600 leading-relaxed">
                Meet current electrical regulations and building standards required for property sales and rentals in Watford.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                💰
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Insurance Requirements</h3>
              <p className="text-gray-600 leading-relaxed">
                Many insurance companies require modern consumer units for home insurance coverage in Watford and surrounding areas.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                🏠
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Property Value</h3>
              <p className="text-gray-600 leading-relaxed">
                Increase your Watford property value with a modern, certified electrical system that appeals to buyers and tenants.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                🔌
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Modern Capacity</h3>
              <p className="text-gray-600 leading-relaxed">
                Support modern electrical demands with adequate circuit capacity for appliances and technology in your Watford home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] to-[#0F2847]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Beyond Electrical?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Trusted consumer unit specialists in Watford
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">🏆</div>
              <h3 className="text-xl font-bold text-white mb-2">15 Years Experience</h3>
              <p className="text-gray-300 text-sm">
                Expert consumer unit installations
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">✅</div>
              <h3 className="text-xl font-bold text-white mb-2">NICEIC Approved</h3>
              <p className="text-gray-300 text-sm">
                Fully certified electricians
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="text-xl font-bold text-white mb-2">Same Day Service</h3>
              <p className="text-gray-300 text-sm">
                Fast installation available
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">💯</div>
              <h3 className="text-xl font-bold text-white mb-2">Fixed Pricing</h3>
              <p className="text-gray-300 text-sm">
                No hidden costs
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-6">
            Consumer Unit Upgrades Across Watford
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Professional consumer unit installation throughout Watford, Rickmansworth, and surrounding areas
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {areas.map((area) => (
              <Link
                key={area.slug}
                to={`/services/installations/consumer-unit-upgrades/${area.slug}`}
                className="bg-gradient-to-r from-[#FFD700] to-[#FFC700] text-[#0A1628] px-5 py-2.5 rounded-full font-semibold text-sm shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer"
              >
                {area.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Need a Consumer Unit Upgrade in Watford?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 leading-relaxed">
            Contact us today for a free assessment and quote. Our NICEIC electricians will inspect your current fuse box and recommend the best solution for your Watford property.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-2xl shadow-[#FFD700]/40 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call 020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill text-xl"></i>
              Get Free Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}